import React from 'react';
import dayjs from 'dayjs';

import Chip from '~components/atoms/Chip';

import { TGetHeadersReturnType } from '~types/utils';
import { BinaryStatusEnum, BranchStatusEnum, CustomerAccStatusEnum, EarmarkReleaseRequestStatusEnum, ProjectAccStatusEnum, ProjectChangeHistoryStatusEnum, TransactionStatusEnum, TransferStatusEnum } from './enums';

const Auth = {
    storeAuthToken: (authToken: string): void => {
        sessionStorage.setItem('authToken', authToken);
    },
    getAuthToken: (): string | null => {
        return sessionStorage.getItem('authToken');
    },
    clearAuthToken: (): void => {
        sessionStorage.removeItem('authToken');
    },
};

const API = {
    getHeaders: (authToken: string, timeout?: number): TGetHeadersReturnType => ({
        headers: {
            Authorization: `Bearer ${authToken}`,
        },
        timeout,
    }),
};

const SessionStorage = {
    getItem<T>(key: string): T | null {
        const storageItem = sessionStorage.getItem(key);

        if (!storageItem) return null;

        return JSON.parse(storageItem);
    },
    setItem: (key: string, item: unknown | null): 'Success' | 'Failed' => {
        if (!item) return 'Failed';
        const parse = JSON.stringify(item);

        sessionStorage.setItem(key, parse);
        return 'Success';
    },
    removeItem: (key: string): void => {
        sessionStorage.removeItem(key);
    },
};

const Misc = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    debounce: <F extends (...args: any[]) => any>(
        func: F,
        waitFor: number
    ): ((...args: Parameters<F>) => void) => {
        let timeout: number | null = null;

        const debounced = (...args: Parameters<F>) => {
            if (timeout !== null) clearTimeout(timeout);

            timeout = window.setTimeout(() => {
                func(...args);
            }, waitFor);
        };

        return debounced;
    },

    getEnumValueByKey<T extends { [key: string]: string | number }>(
        enumObject: T,
        key: string
    ): T[keyof T] | undefined {
        // Type assertion is used here because TypeScript doesn't recognize
        // that the string index is valid for all enums
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (enumObject as any)[key];
    },

    renderCustomerStatusChip(
        type: CustomerAccStatusEnum,
        label: string,
        chipClassName?: string,
    ): JSX.Element {
        let chip: JSX.Element;

        switch (type) {
            case CustomerAccStatusEnum.Active: {
                chip = (
                    <Chip
                        type='success'
                        label={label}
                        className={chipClassName}
                    />
                );
                break;
            }

            case CustomerAccStatusEnum.Deactivated: {
                chip = (
                    <Chip
                        type='inactive'
                        label={label}
                        className={chipClassName}
                    />
                );
                break;
            }

            default: chip = <Chip label={label} />;
        }

        return chip;
    },

    renderPendingReleaseEarmarkStatusChip(
        type: EarmarkReleaseRequestStatusEnum,
        label: string,
        chipClassName?: string,
    ): JSX.Element {
        let chip: JSX.Element;

        switch (type) {
            case EarmarkReleaseRequestStatusEnum.Pending: {
                chip = (
                    <Chip
                        type='warning'
                        label={label}
                        className={chipClassName}
                    />
                );
                break;
            }

            case EarmarkReleaseRequestStatusEnum.Approved: {
                chip = (
                    <Chip
                        type='inactive'
                        label={label}
                        className={chipClassName}
                    />
                );
                break;
            }

            case EarmarkReleaseRequestStatusEnum.Rejected: {
                chip = (
                    <Chip
                        type='inactive'
                        label={label}
                        className={chipClassName}
                    />
                );
                break;
            }

            default: chip = <Chip label={label} />;
        }

        return chip;
    },

    renderProjectStatusChip(
        type: ProjectAccStatusEnum,
        label: string,
        chipClassName?: string,
    ): JSX.Element {
        let chip: JSX.Element;

        switch (type) {
            case ProjectAccStatusEnum.Active: {
                chip = (
                    <Chip
                        type='success'
                        label={label}
                        className={chipClassName}
                    />
                );
                break;
            }

            case ProjectAccStatusEnum.Deactivated: {
                chip = (
                    <Chip
                        type='error'
                        label={label}
                        className={chipClassName}
                    />
                );
                break;
            }

            default: chip = <Chip label={label} />;
        }

        return chip;
    },

    renderPendingProjectStatusChip(
        type: ProjectChangeHistoryStatusEnum,
        label: string,
        chipClassName?: string,
    ): JSX.Element {
        let chip: JSX.Element;

        switch (type) {
            case ProjectChangeHistoryStatusEnum.Pending: {
                chip = (
                    <Chip
                        type='warning'
                        label={label}
                        className={chipClassName}
                    />
                );
                break;
            }

            case ProjectChangeHistoryStatusEnum.Approved: {
                chip = (
                    <Chip
                        type='inactive'
                        label={label}
                        className={chipClassName}
                    />
                );
                break;
            }

            case ProjectChangeHistoryStatusEnum.Rejected: {
                chip = (
                    <Chip
                        type='inactive'
                        label={label}
                        className={chipClassName}
                    />
                );
                break;
            }

            default: chip = <Chip label={label} />;
        }

        return chip;
    },

    renderPendingTransferStatusChip(
        type: TransferStatusEnum,
        label: string,
        chipClassName?: string,
    ): JSX.Element {
        let chip: JSX.Element;

        switch (type) {
            case TransferStatusEnum.PendingDeliver: {
                chip = (
                    <Chip
                        type='warning'
                        label={label}
                        className={chipClassName}
                    />
                );
                break;
            }

            case TransferStatusEnum.PendingReceive: {
                chip = (
                    <Chip
                        type='warning'
                        label={label}
                        className={chipClassName}
                    />
                );
                break;
            }

            case TransferStatusEnum.Approved: {
                chip = (
                    <Chip
                        type='success'
                        label={label}
                        className={chipClassName}
                    />
                );
                break;
            }

            case TransferStatusEnum.RejectedDeliver: {
                chip = (
                    <Chip
                        type='error'
                        label={label}
                        className={chipClassName}
                    />
                );
                break;
            }

            case TransferStatusEnum.RejectedReceive: {
                chip = (
                    <Chip
                        type='error'
                        label={label}
                        className={chipClassName}
                    />
                );
                break;
            }

            default: chip = <Chip label={label} />;
        }

        return chip;
    },

    renderBinaryStatusChip<T>(
        type: T,
        label: string,
        chipClassName?: string,
    ): JSX.Element {
        let chip: JSX.Element;

        switch (type) {
            case BinaryStatusEnum.Active: {
                chip = (
                    <Chip
                        type='success'
                        label={label}
                        className={chipClassName}
                    />
                );
                break;
            }

            case BinaryStatusEnum.Deactivated: {
                chip = (
                    <Chip
                        type='inactive'
                        label={label}
                        className={chipClassName}
                    />
                );
                break;
            }

            default: chip = <Chip label={label} />;
        }

        return chip;
    },

    simulateApi<T>(response: T): Promise<T> {
        return new Promise(resolve => {
            setTimeout(() => {
                resolve(response);
            }, 1000);
        });
    },

    renderTransactionStatusChip(
        type: TransactionStatusEnum,
        label: string
    ): JSX.Element {
        let chip: JSX.Element;

        switch (type) {
            case TransactionStatusEnum.Pending: {
                chip = <Chip type='warning' label={label} />;
                break;
            }

            case TransactionStatusEnum.Approved: {
                chip = <Chip type='success' label={label} />;
                break;
            }

            case TransactionStatusEnum.Rejected: {
                chip = <Chip type='error' label={label} />;
                break;
            }

            case TransactionStatusEnum.Cancelled: {
                chip = <Chip type='inactive' label={label} />;
                break;
            }

            default: chip = <Chip label={label} />;
        }

        return chip;
    },

    renderApprovalStatusChip(
        type: number,
        label: string
    ): JSX.Element {
        let chip: JSX.Element;

        switch (type) {
            case TransactionStatusEnum.Pending: {
                chip = <Chip type='warning' label={label} />;
                break;
            }

            case TransactionStatusEnum.Approved: {
                chip = <Chip type='success' label={label} />;
                break;
            }

            case TransactionStatusEnum.Rejected: {
                chip = <Chip type='error' label={label} />;
                break;
            }

            case TransactionStatusEnum.Cancelled: {
                chip = <Chip type='inactive' label={label} />;
                break;
            }

            default: chip = <Chip label={label} />;
        }

        return chip;
    },

    dateTimeFormat: (date: string) => {
        return dayjs(date).format('DD MMM YYYY hh:mm A');
    },

    dateFormat: (date: string) => {
        return dayjs(date).format('DD MMM YYYY');
    },

    fixedDecimal: (value: number | string, decimal: number) => {
        if (decimal === 2 ){
            const fixedValue = (Math.round(Number(value) * 100) / 100).toFixed(decimal);
            return fixedValue;
        }

        if (decimal === 3 ){
            const fixedValue = (Math.round(Number(value) * 1000) / 1000).toFixed(decimal);
            return fixedValue;
        }
    },

    blockEnterKey: (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            return;
        }
    },

    renderBranchStatusChip<T>(
        type: T,
        label: string,
        chipClassName?: string,
    ): JSX.Element {
        let chip: JSX.Element;

        switch (type) {
            case BranchStatusEnum.Active: {
                chip = (
                    <Chip
                        type='success'
                        label={label}
                        className={chipClassName}
                    />
                );
                break;
            }

            case BranchStatusEnum.Deactivated: {
                chip = (
                    <Chip
                        type='inactive'
                        label={label}
                        className={chipClassName}
                    />
                );
                break;
            }

            case BranchStatusEnum.Deleted: {
                chip = (
                    <Chip
                        type='error'
                        label={label}
                        className={chipClassName}
                    />
                );
                break;
            }

            default: chip = <Chip label={label} />;
        }

        return chip;
    },

    sanitizeReferralCode: (field: string) => {
        if (field === null || field === undefined) {
            return field;
        }

        if (typeof field !== 'string') {
            return field;
        }

        let value = field.trimStart();

        if (
            /^[=+\-@]/.test(value)
            || value.startsWith('\t')
            || value.startsWith('\r')
            || /^0x0[0-9A-Fa-f]/i.test(value) // covers common control hexes
        ) {
            value = `'${value}`;
        }

        return value;
    },
};

const Styles = {
    concatClassNames: (classNames: Array<string | undefined | null>) => {
        return classNames.filter(style => style).join(' ');
    },
};

export default {
    Auth,
    API,
    SessionStorage,
    Misc,
    Styles,
};
